import { useState, useEffect, useRef } from "react";
import api, { BACKEND_URL, formatApiErrorDetail } from "../../lib/api";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Camera, X, Calendar, Upload, ChevronDown } from "lucide-react";

const resolveImg = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const formatSince = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

/**
 * Editorial editor for a single Master in Residence row.
 * Expands inline beneath the artist's pinned card in AdminFeatured.
 */
export function MasterEditor({ artist, masterMeta, open, onToggle, onSaved }) {
  const meta = masterMeta || {};
  const [blurb, setBlurb] = useState(meta.blurb || "");
  const [spotlightUrl, setSpotlightUrl] = useState(meta.spotlight_url || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Reset local state when the row's meta changes from outside (after save)
  useEffect(() => {
    setBlurb(meta.blurb || "");
    setSpotlightUrl(meta.spotlight_url || "");
  }, [artist.id, meta.blurb, meta.spotlight_url]);

  const pickSpotlight = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB)");
      e.target.value = "";
      return;
    }
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(f.type)) {
      toast.error("PNG, JPEG, WebP or GIF only");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSpotlightUrl(data.url);
      toast.success("Spotlight uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { blurb, spotlight_url: spotlightUrl };
      const { data } = await api.put(`/admin/masters/${artist.id}`, payload);
      toast.success("Editorial saved");
      onSaved?.(artist.id, data.master_meta);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Reset editorial for ${artist.name}? Their bio + avatar will be used on the homepage.`)) return;    setSaving(true);
    try {
      await api.delete(`/admin/masters/${artist.id}`);
      setBlurb("");
      setSpotlightUrl("");
      toast.success("Editorial cleared");
      onSaved?.(artist.id, null);
    } catch (err) {
      toast.error("Could not clear");
    } finally {
      setSaving(false);
    }
  };

  const previewSrc = resolveImg(spotlightUrl || artist.avatar_url) || "https://images.unsplash.com/photo-1500336624523-d727130c3328?w=400";

  return (
    <div data-testid={`master-editor-${artist.id}`} className="bg-white border border-t-0 border-[#E5E0D8]">
      <button
        type="button"
        onClick={onToggle}
        data-testid={`master-editor-toggle-${artist.id}`}
        className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-[#F4EFE6]/40 transition-colors"
      >
        <span className="label-eyebrow text-[#1E3A8A]">
          {meta.blurb || meta.spotlight_url ? "— Editorial set" : "— Add editorial"}
          {meta.since && (
            <span className="ml-3 font-mono-aam text-xs text-[#555] normal-case">
              <Calendar className="inline w-3 h-3 mr-1" strokeWidth={1.5} />
              since {formatSince(meta.since)}
            </span>
          )}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
        />
      </button>

      {open && (
        <div className="p-4 border-t border-[#E5E0D8] grid grid-cols-1 md:grid-cols-[120px_1fr] gap-4">
          {/* Spotlight photo */}
          <div className="flex flex-col items-center md:items-start gap-2">
            <div className="relative w-[110px] aspect-square border border-[#E5E0D8] bg-[#F4EFE6] overflow-hidden">
              <img
                src={previewSrc}
                alt={artist.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = "https://images.unsplash.com/photo-1500336624523-d727130c3328?w=400";
                }}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid={`master-spotlight-upload-${artist.id}`}
                disabled={uploading}
                aria-label="Upload spotlight"
                className="absolute -bottom-1 -right-1 bg-[#1E3A8A] text-white p-1.5 border border-white hover:bg-[#162a66] transition-colors disabled:opacity-50"
              >
                {uploading ? <Upload className="w-3 h-3 animate-pulse" /> : <Camera className="w-3 h-3" strokeWidth={2} />}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={pickSpotlight}
                className="hidden"
                data-testid={`master-spotlight-file-${artist.id}`}
              />
            </div>
            {spotlightUrl && (
              <button
                type="button"
                onClick={() => setSpotlightUrl("")}
                data-testid={`master-spotlight-clear-${artist.id}`}
                className="label-eyebrow text-[10px] text-[#D32F2F] hover:underline inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" strokeWidth={2} /> Use default
              </button>
            )}
            <p className="text-[10px] text-[#555] font-mono-aam max-w-[110px] leading-tight">
              Override the homepage photo. Defaults to the artist&rsquo;s avatar.
            </p>
          </div>

          {/* Blurb */}
          <div>
            <label className="label-eyebrow text-[#555]">Editorial blurb</label>
            <Textarea
              data-testid={`master-blurb-${artist.id}`}
              rows={3}
              maxLength={400}
              value={blurb}
              onChange={(e) => setBlurb(e.target.value)}
              placeholder="One-line editorial framing — e.g. &lsquo;A practice that listens to the ancestors.&rsquo;"
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
            <p className="text-[10px] text-[#888] mt-1 font-mono-aam">{blurb.length}/400 · falls back to the artist&rsquo;s bio if empty</p>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                onClick={save}
                disabled={saving || uploading}
                data-testid={`master-save-${artist.id}`}
                className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-5 py-2 h-auto label-eyebrow disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save editorial"}
              </Button>
              {(meta.blurb || meta.spotlight_url) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={clearAll}
                  disabled={saving}
                  data-testid={`master-clear-${artist.id}`}
                  className="rounded-none border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white label-eyebrow px-5 py-2 h-auto"
                >
                  Reset to defaults
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MasterEditor;
